import { inject, injectable } from 'inversify'
import type { ErrorEvent as SentryErrorEvent, Integration, LogSeverityLevel } from '@sentry/core'
import type { EventHint, ReplayBreadcrumbFrame, ReplayFrameEvent, ReplaySpanFrame, SeverityLevel } from '@sentry/vue'
import type { Router } from 'vue-router'
// eslint-disable-next-line tp/forbid-import-composable-to-service,tp/using-vue-in-services-restriction
import type { App } from '@/composition/vue/compositionApi'
import { LogLevel } from '@/core/logger/types'
import type { TSentry, MonitoringServiceInterface } from '@/services/monitoring/types'
import type { Config } from '@/core/types'
import { SERVICE_TYPES } from '@/core/container/types'
import TmAbsurdError from '@/core/error/tmAbsurdError'
import type { Dict } from '@/types'
import { TmBaseError } from '@/core/error/tmBaseError'
import { getSafariVersion, isMobile } from '@/utils/isMobile'
import type User from '@/data/models/domain/User'
import type { MicroSentryMonitoringService } from '@/services/monitoring/microSentryMonitoringService'
import type TranslateService from '@/services/translateService'
import type UserSettings from '@/data/models/domain/UserSettings'
import type SubscriptionService from '@/services/transport/subscriptionService'
import { ROUTER_BEFORE_EACH, type Route } from '@/services/route/types'
import type { TmRoutes } from '@/services/route/routerTypes'
import { AutomationRoutes } from '@/routes/user/automations/types'
import { SENTRY_IGNORE_ERRORS } from '@/services/monitoring/ignoreErrors'
import { getAssetLoadingFingerprint } from '@/services/monitoring/assetLoadingErrors'
import type { FeatureFlagsService } from '@/services/featureFlagsService'
import { FeatureFlags } from '@/services/types'

/** Non-enumerable key used to pass render diagnostics from the Vue error handler to `beforeSend`. */
const BROKEN_VUE_COMPONENTS_KEY = '__tmBrokenVueComponents'
/** Hard cap for the render tree walk, so the diagnostics can never hang the error handler. */
const BROKEN_VUE_COMPONENTS_VISIT_LIMIT = 20000
/** How many ancestors to report for every broken component. */
const BROKEN_VUE_COMPONENTS_CHAIN_LIMIT = 10
/** Without an upper bound the idle callback never runs in a background tab, leaving micro-sentry in charge. */
const MAX_IDLE_WAIT_BEFORE_SENTRY_LOAD = 10_000

/**
 * Minimal structural shape of the Vue internals the diagnostics below rely on.
 * `ComponentInternalInstance` is not re-exported by the project's composition API barrel, and only
 * these few fields are needed, so a local type keeps the coupling to Vue internals explicit.
 */
type VueRenderedVNode = {
  el: unknown
  component?: VueComponentInstance | null
  children?: unknown
}

type VueComponentInstance = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  type?: { name?: string; __name?: string }
  parent?: VueComponentInstance | null
  subTree?: VueRenderedVNode | null
  isMounted?: boolean
}

@injectable()
export default class SentryMonitoringService implements MonitoringServiceInterface {
  private Sentry: TSentry

  private user: (User & UserSettings) | null = null

  private isInited = false

  private isVueRenderDiagnosticsAttached = false

  private app: App

  private router?: Router

  private replayIntegration: Integration

  private browserTracing: Integration

  constructor(
    @inject(SERVICE_TYPES.Config) protected readonly config: Config,
    @inject(SERVICE_TYPES.MicroSentryMonitoringService)
    protected readonly microSentryService: MicroSentryMonitoringService,
    @inject(SERVICE_TYPES.TranslateService)
    protected readonly translateService: TranslateService,
    @inject(SERVICE_TYPES.SubscriptionService) protected readonly subscriptionService: SubscriptionService,
    @inject(SERVICE_TYPES.FeatureFlagsService) protected readonly featureFlagsService: FeatureFlagsService,
  ) {}

  private feedbackEnabledRouteNames: Array<TmRoutes['name']> = [
    AutomationRoutes.flows,
    AutomationRoutes.flowDetails,
    AutomationRoutes.templates,
    AutomationRoutes.logs,
  ]

  public init(app: App, router?: Router): void {
    if (this.isInited) {
      return
    }
    this.app = app
    this.router = router
    // This is a temporary micro-client (~30KB) while the main client (Sentry, ~700KB) is loaded dynamically
    this.microSentryService.init(app)

    requestIdleCallback(
      async () => {
        try {
          this.Sentry = await import('@sentry/vue')
        } catch {
          // Micro-sentry stays the only client for the rest of the session
          this.microSentryService.logError(new Error('Sentry SDK failed to load'))
          return
        }

        this.microSentryService.destroy()

        this.initSentry()
        if (this.featureFlagsService.isFeatureEnabled(FeatureFlags.SentryVerboseLogging)) {
          this.attachVueRenderDiagnostics(app)
        }
        this.isInited = true
        if (this.isFeedbackButtonEnabled) {
          this.customizeWidgetButton()
          this.subscriptionService.subscribe(
            ROUTER_BEFORE_EACH,
            (to: Route) => {
              if (typeof to.name === 'string' && this.feedbackEnabledRouteNames.find((t) => t === to.name)) {
                this.toggleWidgetButton(true)
              } else {
                this.toggleWidgetButton(false)
              }
            },
            true,
          )
        }

        // A user set while the SDK was loading has not reached Sentry yet
        this.Sentry.setUser(this.user)
      },
      { timeout: MAX_IDLE_WAIT_BEFORE_SENTRY_LOAD },
    )
  }

  public async setUser(user: (User & UserSettings) | null) {
    const wasProfilingEnabled = this.isProfilingEnabled
    this.user = user

    if (!this.Sentry) {
      this.microSentryService.setUser(user)
      return
    }

    // Profiling options are baked into the client at init time, so they need a fresh one
    if (this.isProfilingEnabled !== wasProfilingEnabled) {
      let closeError: unknown
      try {
        await this.Sentry.close(this.config.sentry.timeout)
      } catch (error) {
        closeError = error
      }

      // A client left closed reports nothing for the rest of the session
      this.initSentry()

      if (closeError) {
        this.Sentry.captureException(closeError)
      }
    }

    // A newer call may have replaced the user while the client was closing
    this.Sentry.setUser(this.user)
  }

  public logInfo(message: string, logLevel: LogLevel = LogLevel.INFO, data?: Dict) {
    if (logLevel === LogLevel.NULL) return
    if (this.Sentry) {
      this.Sentry.logger[this.mapLogLevelToLogSeverityLevel(logLevel)](message, data)
    } else {
      this.microSentryService.logInfo(message, logLevel)
    }
  }

  public logError(error: Error, logLevel: LogLevel = LogLevel.ERROR, data?: Dict) {
    if (logLevel === LogLevel.NULL) return
    if (error instanceof TmBaseError && !error.shouldBeMonitored()) {
      return
    }
    if (this.Sentry) {
      this.Sentry.captureException(error, {
        level: this.mapLogLevelToSentryLogLevel(logLevel),
        extra: data,
      })
    } else {
      this.microSentryService.logError(error, logLevel)
    }
  }

  public destroy() {
    if (this.Sentry) {
      this.Sentry.close()
    } else {
      this.microSentryService.destroy()
    }
  }

  public toggleWidgetButton(display: boolean) {
    const element = this.getWidgetButtonElement()
    if (!element) return
    element.style.display = display ? 'flex' : 'none'
  }

  public getSentryInstance(): TSentry | null {
    return this.Sentry ?? null
  }

  private get isProfilingEnabled(): boolean {
    if (!this.user) {
      return false
    }

    // The flag comes from user settings, which may not be defined yet, so it's not always a boolean
    return this.user.uiProfiling === true
  }

  private getIntegrations(app: App, router?: Router) {
    const integrations: Integration[] = []

    integrations.push(
      this.Sentry.vueIntegration({
        app,
        tracingOptions: {
          trackComponents: false,
          hooks: ['mount', 'update', 'unmount'],
          timeout: this.config.sentry.timeout, // milliseconds
        },
      }),
    )

    integrations.push(
      this.Sentry.breadcrumbsIntegration({
        // This integration is producing a TON of warnings
        console: false,
      }),
    )

    if (router) {
      if (!this.browserTracing) {
        this.browserTracing = this.Sentry.browserTracingIntegration({
          router,
          routeLabel: 'name',
          enableInp: true,
          enableLongTask: true,
          consistentTraceSampling: true,
        })
      }
      integrations.push(this.browserTracing)
    }
    if (this.isProfilingEnabled) {
      integrations.push(this.Sentry.browserProfilingIntegration())
    }

    integrations.push(
      this.Sentry.httpClientIntegration({
        // This array can contain tuples of `[begin, end]` (both inclusive),
        // single status codes, or a combination of both.
        // default: [[500, 599]]
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [/.*/], // default value but required in type
      }),
    )

    // We need to check env variables directly for proper tree-shaking
    // See https://bitbucket.org/textmagicadmin/tp-frontend/pull-requests/2701 for details
    if (import.meta.env.VUE_APP_SENTRY_ENABLE_REPLAY === 'true') {
      if (!this.replayIntegration) {
        this.replayIntegration = this.Sentry.replayIntegration({
          mask: this.config.sentry.maskSelectors,
          unmask: this.config.sentry.unMaskSelectors,
          networkDetailAllowUrls: [this.config.apiUrl],
          beforeErrorSampling: (event) => {
            // Ignore script loading errors
            const errorValue = event.exception?.values?.[0]?.value
            if (!errorValue) {
              return true
            }

            return !this.shouldIgnoreScriptLoadingError(errorValue)
          },
          beforeAddRecordingEvent: (event: ReplayFrameEvent) => {
            // Handle network requests
            if (event.data.tag === 'performanceSpan') {
              const payload = event.data.payload
              if (payload?.op === 'resource.fetch' || payload?.op === 'resource.xhr') {
                this.sanitizeNetworkRequest(payload)
              }
            }
            // Handle breadcrumb events that might contain sensitive data
            else if (event.data.tag === 'breadcrumb') {
              this.sanitizeConsoleLog({ payload: event.data.payload })
            }

            return event
          },
        })
      }
      integrations.push(this.replayIntegration)
    }

    if (this.isFeedbackButtonEnabled && !isMobile()) {
      integrations.push(
        this.Sentry.feedbackIntegration({
          colorScheme: 'light',
          showBranding: false,
          showEmail: true,
          triggerLabel: this.translateService.t('sentry.feedback'),
          emailLabel: this.translateService.t('sentry.emailLabel'),
          showName: false,
          submitButtonLabel: this.translateService.t('sentry.sendFeedback'),
          formTitle: this.translateService.t('sentry.feedbackAndIdeas'),
          messagePlaceholder: this.translateService.t('sentry.messagePlaceholder'),
        }),
      )
    }

    integrations.push(
      this.Sentry.thirdPartyErrorFilterIntegration({
        // Specify the application keys that you specified in the Sentry bundler plugin
        filterKeys: ['textmagic-web-app'],
        // Defines how to handle errors that contain third party stack frames.
        // Possible values are:
        // - 'drop-error-if-contains-third-party-frames'
        // - 'drop-error-if-exclusively-contains-third-party-frames'
        // - 'apply-tag-if-contains-third-party-frames'
        // - 'apply-tag-if-exclusively-contains-third-party-frames'
        behaviour: 'drop-error-if-contains-third-party-frames',
      }),
    )

    integrations.push(this.Sentry.consoleLoggingIntegration({ levels: ['error', 'trace', 'warn'] }))

    return integrations
  }

  private initSentry() {
    this.Sentry.init({
      integrations: this.getIntegrations(this.app, this.router),
      debug: import.meta.env.VUE_APP_SENTRY_DEBUG === 'true',
      app: [this.app],
      dsn: this.config.sentry.dsn,
      environment: APP_BUILD_ENV,
      release: [APP_NAME, APP_VERSION].join('@'),
      enableLogs: true,

      tracePropagationTargets: ['localhost', this.config.appUrl],

      // Capture Replay for 0% of all sessions,
      // plus for 100% of sessions with an error
      replaysSessionSampleRate: 0.0,
      replaysOnErrorSampleRate: 1.0,

      // This option is required for capturing headers and cookies.
      sendDefaultPii: false,

      // Configuration option in Sentry's SDK that controls transaction sampling for performance monitoring
      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampler: ({ _name, _attributes, inheritOrSampleWith }) => {
        if (this.isProfilingEnabled) {
          return 1.0
        }
        // Collect metrics for 30% of users
        return inheritOrSampleWith(0.3)
      },
      // Configuration option in Sentry's JavaScript SDKs that controls the sampling rate for performance profiling.
      // It's a number between 0 and 1 that determines what percentage of sampled transactions will be profiled.
      // A value of 0 means 0% of transactions will be profiled
      // A value of 1 means 100% of transactions will be profiled
      // Any value in between represents the corresponding percentage (e.g., 0.5 means 50%)
      // This setting is relative to the tracing sample rate. For example, if your tracing sample rate is 0.2 (20% of transactions)
      // and your profilesSampleRate is 0.5, then 50% of that 20% (effectively 10% of all transactions) will be profiled.
      profilesSampleRate: this.isProfilingEnabled ? 1 : 0,

      // configuration option in Sentry that controls the sampling of error events sent to Sentry
      // sampleRate is used for Error events
      // tracesSampleRate is used for Transaction events (performance monitoring)
      // temporary increasing sampleRate for investigation a Web Vitals feature
      sampleRate: 0.3,

      ignoreErrors: SENTRY_IGNORE_ERRORS,

      // https://textmagic.atlassian.net/browse/CAR-17199
      // MNTN tracking service do dozens of API calls and create N+1 issue in sentry
      // Every call looks like random IP address + '/is?cb=' + int
      // So lets check this string in URL to detect MNTN call and ignore it
      ignoreSpans: [
        {
          op: 'http.client',
          name: /GET https:\/\/[\d.]+\/is$/,
        },
      ],

      beforeSend: (event, hint?: EventHint) => {
        if (event.request?.data) {
          event.request.data = this.sanitize(event.request.data as Dict)
        }

        if (hint?.originalException instanceof CustomEvent && hint.originalException.type === 'unhandledrejection') {
          return null
        }
        if (hint?.originalException instanceof PromiseRejectionEvent) {
          const reason = hint.originalException.reason
          // ignore Sentry error if reason is not an instance of Error
          if (!reason || !(reason instanceof Error)) {
            return null
          }
          if (reason instanceof TmBaseError && !reason.shouldBeMonitored()) {
            return null
          }
        }
        if (
          hint?.originalException &&
          hint.originalException instanceof TmBaseError &&
          !hint.originalException.shouldBeMonitored()
        ) {
          // Will NOT be logged into Sentry

          // TODO: Add logging to the app logger
          // this.loggerService.log('warn', `An "${hint.originalException.name}" error was occurred. It was not sent to Sentry because it is marked as non-monitored.`)
          return null
        }

        if (hint?.originalException instanceof TmBaseError) {
          const sentryFingerprint = hint.originalException.getSentryFingerprint()
          if (sentryFingerprint) {
            event.fingerprint = sentryFingerprint
          }
        }

        if (!event.fingerprint) {
          const assetFingerprint = getAssetLoadingFingerprint(event.exception?.values?.[0]?.value ?? '')
          if (assetFingerprint) {
            event.fingerprint = assetFingerprint
          }
        }

        if (this.featureFlagsService.isFeatureEnabled(FeatureFlags.SentryVerboseLogging)) {
          if (!event.fingerprint) {
            const vueFingerprint = this.getVueErrorFingerprint(event)
            if (vueFingerprint) {
              event.fingerprint = vueFingerprint
            }
          }

          const brokenVueComponents = this.readBrokenComponents(hint?.originalException)
          if (brokenVueComponents) {
            event.extra = {
              ...event.extra,
              brokenVueComponents,
            }
          }
        }

        // Will be logged to Sentry
        return event
      },

      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
      ],
    })
  }

  /**
   * Errors thrown inside the Vue renderer carry a stack that consists of `@vue/runtime-*` frames only.
   * Once source maps are applied none of these frames is `in_app`, so the default grouping has nothing
   * application specific left to hash and unrelated crashes get merged into a single issue.
   * Grouping such events by the failing component and the error message keeps them apart.
   */
  private getVueErrorFingerprint(event: SentryErrorEvent): string[] | null {
    const exception = event.exception?.values?.[0]
    if (exception?.mechanism?.type !== 'vue') {
      return null
    }

    const componentName = (event.contexts?.vue as { componentName?: string } | undefined)?.componentName
    if (!componentName) {
      return null
    }

    return ['vue', componentName, exception.type ?? 'Error', exception.value ?? '']
  }

  /**
   * Vue renderer crashes (`Cannot read properties of null (reading 'parentNode')` and alike) surface long
   * after the render tree got corrupted: the component Sentry reports is merely the owner of the flushed
   * update job, and Vue's own component trace walks upwards, so the actually broken descendant stays unknown.
   * The invariant that breaks is "a mounted component whose rendered subtree has no DOM node" — it can be
   * located by walking the tree while the error is being handled.
   *
   * The result is attached to the error itself and picked up by `beforeSend`: Sentry captures the exception
   * in a `setTimeout`, while the handler below runs synchronously, so the data is always in place by the
   * time the event is built.
   */
  private attachVueRenderDiagnostics(app: App) {
    if (this.isVueRenderDiagnosticsAttached) {
      return
    }
    this.isVueRenderDiagnosticsAttached = true

    // Sentry has already replaced the handler at this point, so calling it keeps the existing behaviour intact.
    const nextErrorHandler = app.config.errorHandler
    app.config.errorHandler = (err, instance, info) => {
      this.markBrokenComponents(err, app)
      nextErrorHandler?.call(app, err, instance, info)
    }
  }

  private markBrokenComponents(error: unknown, app: App) {
    if (!error || typeof error !== 'object') {
      return
    }

    try {
      const brokenComponents = this.findMountedComponentsWithoutDom(app)
      if (!brokenComponents.length) {
        return
      }

      Object.defineProperty(error, BROKEN_VUE_COMPONENTS_KEY, {
        value: brokenComponents,
        enumerable: false,
        configurable: true,
        writable: true,
      })
    } catch {
      // Diagnostics must never break the error handling chain
    }
  }

  private readBrokenComponents(originalException: unknown): string[] | null {
    if (!originalException || typeof originalException !== 'object') {
      return null
    }

    const value = (originalException as Record<string, unknown>)[BROKEN_VUE_COMPONENTS_KEY]

    return Array.isArray(value) && value.length ? (value as string[]) : null
  }

  /**
   * Walks the rendered tree from the app root and collects every mounted component whose subtree
   * has no DOM node — such a component is the one that will throw on its next update.
   */
  private findMountedComponentsWithoutDom(app: App): string[] {
    // `_instance` is a Vue internal, hence the structural cast
    const root = app._instance as unknown as VueComponentInstance | null
    if (!root) {
      return []
    }

    const broken: string[] = []
    const visitedInstances = new Set<VueComponentInstance>()
    const instances: VueComponentInstance[] = [root]
    let visitsLeft = BROKEN_VUE_COMPONENTS_VISIT_LIMIT

    while (instances.length && visitsLeft > 0) {
      const instance = instances.pop() as VueComponentInstance
      if (visitedInstances.has(instance)) {
        continue
      }
      visitedInstances.add(instance)

      const subTree = instance.subTree
      if (!subTree) {
        continue
      }
      if (instance.isMounted && subTree.el === null) {
        broken.push(this.formatComponentChain(instance))
      }

      // Descend through the rendered vnodes until the next component boundary:
      // everything a child component renders lives in its own subtree.
      const vnodes: VueRenderedVNode[] = [subTree]
      while (vnodes.length && visitsLeft > 0) {
        visitsLeft -= 1
        const vnode = vnodes.pop() as VueRenderedVNode
        if (vnode.component) {
          instances.push(vnode.component)
        } else if (Array.isArray(vnode.children)) {
          for (const child of vnode.children) {
            if (child && typeof child === 'object' && !Array.isArray(child)) {
              vnodes.push(child as VueRenderedVNode)
            }
          }
        }
      }
    }

    return broken
  }

  private formatComponentChain(instance: VueComponentInstance): string {
    const names: string[] = []
    let current: VueComponentInstance | null | undefined = instance

    while (current && names.length < BROKEN_VUE_COMPONENTS_CHAIN_LIMIT) {
      // `__name` is emitted by the SFC compiler and survives the production build
      names.push(current.type?.name ?? current.type?.__name ?? 'Anonymous')
      current = current.parent
    }

    return names.join(' < ')
  }

  private sanitize(data: Dict) {
    if (!data || typeof data !== 'object') {
      return data
    }

    const sensitiveKeysPattern =
      /(token$|secret$|.*_token$|.*password.*|.*pass.*|key$|.*_key$|auth.*|credential.*|.*pin.*)/i
    const result = { ...data }

    for (const key of Object.keys(data)) {
      if (sensitiveKeysPattern.test(key)) {
        result[key] = '[FILTERED]'
      } else if (typeof data[key] === 'object' && data[key] !== null) {
        result[key] = this.sanitize(data[key] as Dict)
      }
    }

    return result
  }

  private mapLogLevelToSentryLogLevel(logLevel: Exclude<LogLevel, LogLevel.NULL>): SeverityLevel {
    switch (logLevel) {
      case LogLevel.DEBUG:
        return 'debug'
      case LogLevel.LOG:
        return 'log'
      case LogLevel.WARNING:
        return 'warning'
      case LogLevel.ERROR:
        return 'error'
      case LogLevel.INFO:
        return 'info'
      case LogLevel.TRACE:
        return 'info'
      case LogLevel.FATAL:
        return 'fatal'
      default:
        throw new TmAbsurdError(logLevel)
    }
  }

  private mapLogLevelToLogSeverityLevel(logLevel: Exclude<LogLevel, LogLevel.NULL>): LogSeverityLevel {
    switch (logLevel) {
      case LogLevel.TRACE:
        return 'trace'
      case LogLevel.DEBUG:
        return 'debug'
      case LogLevel.INFO:
        return 'info'
      case LogLevel.WARNING:
        return 'warn'
      case LogLevel.ERROR:
        return 'error'
      case LogLevel.FATAL:
        return 'fatal'
      case LogLevel.LOG:
        return 'info'
      default:
        throw new TmAbsurdError(logLevel)
    }
  }

  private customizeWidgetButton() {
    const host = this.getWidgetButtonElement()
    if (!host) return
    host.classList.add('no-print')
    const widthThreshold = 1000
    const ver = getSafariVersion()
    const ua = navigator.userAgent.toLowerCase()
    if ((ua.indexOf('safari/') > -1 && ver && ver < '15.6') || ua.indexOf('firefox/') > -1) {
      // for old Safari & Firefox
      window.addEventListener('resize', () => {
        const display = document.body.offsetWidth > widthThreshold
        this.toggleWidgetButton(display)
      })
    }
    try {
      const styleSheet = this.getStyleSheetForWidgetButton(host)
      if (!styleSheet) return
      this.setRulesForWidgetButton(styleSheet, widthThreshold)
    } catch {
      /* CAR-6039 */
    }
    this.toggleWidgetButton(false)
  }

  private setRulesForWidgetButton(styleSheet: CSSStyleSheet, widthThreshold: number) {
    const rules = [
      `.widget__actor svg {
        display: none !important;
      }`,
      `.widget__actor {
        @media screen and (max-width: ${widthThreshold}px) {
          display: none !important;
        }
        display: flex;
        justify-content: center !important;
        position: fixed !important;
        top: 50% !important;
        bottom: auto !important;
        left: auto !important;
        right: 0 !important;
        padding: 6px 12px !important;
        transform: translateY(-50%) translateX(70%) rotate(-90deg) !important;
        transform-origin: bottom center !important;
        color: #fff !important;
        background-color: #D23201 !important;
        border-radius: 4px 4px 0 0 !important;
        transition: all 0.3s ease !important;
      }`,
      `.widget__actor:hover {
        color: #2b2233 !important;
        background-color: #f6f6f7 !important;
      }`,
      `.widget__actor span {
        font-size: 13px;
        line-height: normal;
        word-break: normal !important;
        word-wrap: normal !important;
        white-space: nowrap !important;
        font-weight: 400;
        font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif !important;
        -webkit-font-smoothing: auto;
      }`,
      `.form__top {
        display: flex !important;
        flex-direction: column-reverse !important;
      }`,
      `.form__label[for="email"] {
        order: 0 !important;
      }`,
      `.form__label[for="message"] {
        order: 1 !important;
      }`,
      `.form__label[for="screnshot"] {
        order: 2 !important;
      }`,
      `.btn--primary {
          background: #008CFF !important;
      }`,
      `.btn--primary:hover {
         background: #008CFF !important;
      }`,
      `.editor__tool--active {
        background: #008CFF !important;
      }`,
    ]
    rules.forEach((rule) => styleSheet?.insertRule(rule))
  }

  private getStyleSheetForWidgetButton(host: Element) {
    const styleSheets = host.shadowRoot?.styleSheets
    if (!styleSheets) return null
    return styleSheets.item(styleSheets.length - 1)
  }

  private getWidgetButtonElement(): HTMLElement | null {
    return document.querySelector('#sentry-feedback')
  }

  private sanitizeNetworkRequest(payload: ReplaySpanFrame): void {
    if (payload.op !== 'resource.fetch' && payload.op !== 'resource.xhr') {
      return
    }

    const data = payload.data
    if (!data || typeof data !== 'object') {
      return
    }

    // Sanitize request data
    if (data.request?.body) {
      try {
        // Handle both string and object body types
        let body: unknown
        let doStringify = false
        if (typeof data.request.body === 'string') {
          body = JSON.parse(data.request.body)
          doStringify = true
        } else {
          body = data.request.body
        }

        // Sanitize sensitive data
        const sanitizedBody = this.sanitize(body as Dict)
        data.request.body = doStringify ? JSON.stringify(sanitizedBody) : sanitizedBody
      } catch (error) {
        this.logInfo('beforeAddRecordingEvent: failed to parse request body for sanitization', LogLevel.DEBUG, {
          error: (error as Error).message,
          body: data.request.body,
        })
      }
    }

    // Sanitize response data
    if (data.response?.body) {
      try {
        // Handle both string and object body types
        let body: unknown
        let doStringify = false
        if (typeof data.response.body === 'string') {
          body = JSON.parse(data.response.body)
          doStringify = true
        } else {
          body = data.response.body
        }

        // Sanitize sensitive data
        const sanitizedBody = this.sanitize(body as Dict)
        data.response.body = doStringify ? JSON.stringify(sanitizedBody) : sanitizedBody
      } catch (error) {
        this.logInfo('beforeAddRecordingEvent: failed to parse response body for sanitization', LogLevel.DEBUG, {
          error: (error as Error).message,
          body: data.response.body,
        })
      }
    }
  }

  private sanitizeConsoleLog({ payload }: { payload: ReplayBreadcrumbFrame }): void {
    if (payload.category !== 'console' || !payload.data || !Array.isArray(payload.data.arguments)) {
      return
    }

    payload.data.arguments = payload.data.arguments.map((arg: unknown) => {
      if (typeof arg === 'object' && arg !== null) {
        try {
          return this.sanitize(arg as Dict)
        } catch {
          return '[SANITIZATION_ERROR]'
        }
      }
      return arg
    })
  }

  private shouldIgnoreScriptLoadingError(errorMessage: string): boolean {
    // Проверяем, что это ошибка загрузки
    if (!/failed to load/i.test(errorMessage)) {
      return false
    }

    // Ищем URL в сообщении об ошибке
    const urlMatch = /https?:\/\/[^)\s]+/.exec(errorMessage)
    if (!urlMatch) {
      return false
    }

    try {
      // Проверяем, что URL не с нашего домена
      const url = new URL(urlMatch[0])
      const appUrl = new URL(this.config.appUrl)
      return url.hostname !== appUrl.hostname
    } catch {
      return false
    }
  }

  private get isFeedbackButtonEnabled(): boolean {
    return import.meta.env.VUE_APP_SENTRY_FEEDBACK === 'true'
  }
}
